import React, { createRef } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FieldTitle from './FieldTitle';
import ThemeIcons from '../../../theme/Icons';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledBarcodeWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.padding || '3px'};
  border: ${(props) => props.borderWidth || 1}px solid ${(props) => props.borderColor || '#ccc'};
  border-radius: ${(props) => `${ props.borderRadius}px` || '4px'};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
`;

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  border: 0px;
  border-radius: ${(props) => `${props.borderRadius}px`};
  border-color: transparent;
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor : 'transparent'};
  font-size: ${(props) => props.fontSize};
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  resize: none;

  border: 1px solid transparent;

  &:hover, &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }

`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  cursor: pointer;
  background: transparent;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class BarcodeGenericField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,

    wrapperStyles: PropTypes.object,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
     this.textareaRef = createRef();
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes } = this.state;

    const {
      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      value,
      disabled,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,

    } = this.props;

    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          <StyledBarcodeWrapper
            borderRadius={borderRadius}
            borderColor={borderColor}
            borderWidth={borderWidth}
            backgroundColor={backgroundColor}
            padding={padding}
          >
            <StyledInput
              ref={this.textareaRef}
              value={changes}
              {...{
                primaryColor,
                disabled,
                fontSize,
                fontColor,
                fontStyle,
                fontWeight,
                textAlign,
                textTransform,
                textDecoration,
                textOverflow,
                borderColor,
                borderRadius,
                borderWidth,
                backgroundColor,
                padding,
              }}
              onChange={(e) => {
                this.setState({ changes: e.target.value });
              }}
              onFocus={(e) => {

                if (!focused) this.setState({ focused: true });

                if (!disabled && onFocus && !focused) {

                  onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });

                }

              }}
              onBlur={(e) => {

                const nextValue = e.target.value;

                if (focused) this.setState({ focused: false });

                if (!disabled && value !== nextValue) {
                  onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
                }

                if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
              }}
            />
            {disabled ? null : (
              <IconWrapper onClick={this.handleBarcodeClick}>
                <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
              </IconWrapper>
            )}
          </StyledBarcodeWrapper>
        </StyledInternalWrapper>
      </StyledWrapper>

    );
  }

  handleBarcodeClick = async () => {

    const { onCapture, onChange, fieldId, fieldIdentifier, fieldPositionId, value } = this.props;

    const response = await onCapture();

    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }


    this.setState({ changes: response }, () => {
      const { changes } = this.state;

      if (onChange && changes !== value) {
        onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
      }
    });
  }
}

export default BarcodeGenericField;
